"use client";

import type { InsightsRenderWidgetKind } from "./parse-genui";
import { cn } from "@/shared/lib/utils/cn";

/**
 * Placeholder shell while an HTML <render> fragment streams (before iframe mount).
 *
 * Caller: InsightsAnswer only, when `seg.complete === false` and GenUI cannot
 * paint yet. Avoids flashing raw markup — user: "The view shows HTML while the
 * render tag is being written… It looks really bad."
 *
 * Kind is inferred from the partial body (table / bar / chart / cards) so the
 * loading animation matches the widget the LLM is writing.
 */
function Bone({ className, delayMs = 0 }: { className?: string; delayMs?: number }) {
  return (
    <div
      className={cn("animate-pulse rounded bg-muted motion-reduce:animate-none", className)}
      style={delayMs ? { animationDelay: `${delayMs}ms` } : undefined}
    />
  );
}

function TitleBone() {
  return (
    <div className="border-b border-border/50 bg-muted/30 px-3.5 py-2.5">
      <Bone className="h-4 w-44" />
    </div>
  );
}

function WritingFooter({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 border-t border-border/40 px-3.5 py-2">
      <span className="relative flex size-2 shrink-0">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary/40 opacity-75 motion-reduce:hidden" />
        <span className="relative inline-flex size-2 rounded-full bg-primary/70" />
      </span>
      <span className="text-[12px] leading-none text-muted-foreground">Writing {label}…</span>
    </div>
  );
}

const COL_WIDTHS = ["w-[28%]", "w-[22%]", "w-[18%]", "w-[24%]"] as const;
const ROW_WIDTHS = [
  ["w-[24%]", "w-[20%]", "w-[16%]", "w-[22%]"],
  ["w-[26%]", "w-[18%]", "w-[20%]", "w-[20%]"],
  ["w-[22%]", "w-[24%]", "w-[14%]", "w-[26%]"],
  ["w-[20%]", "w-[16%]", "w-[22%]", "w-[18%]"],
] as const;

function TableSkeleton() {
  return (
    <>
      <TitleBone />
      <div className="flex gap-3 border-b border-border/40 bg-muted/40 px-3.5 py-2">
        {COL_WIDTHS.map((w, i) => (
          <Bone key={i} className={cn("h-3", w)} delayMs={i * 50} />
        ))}
      </div>
      {ROW_WIDTHS.map((widths, row) => (
        <div key={row} className={cn("flex gap-3 px-3.5 py-2.5", row % 2 === 1 && "bg-muted/20")}>
          {widths.map((w, col) => (
            <Bone key={col} className={cn("h-3", w)} delayMs={100 + row * 80 + col * 40} />
          ))}
        </div>
      ))}
      <WritingFooter label="table" />
    </>
  );
}

const BAR_WIDTHS = ["w-[72%]", "w-[58%]", "w-[84%]", "w-[45%]", "w-[63%]"] as const;

function BarSkeleton() {
  return (
    <>
      <TitleBone />
      <div className="space-y-3 px-3.5 py-3">
        {BAR_WIDTHS.map((w, i) => (
          <div key={i} className="grid grid-cols-[minmax(5.5rem,9rem)_1fr_auto] items-center gap-2">
            <Bone className="h-3 w-16" delayMs={i * 70} />
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <Bone
                className={cn("h-full rounded-full bg-muted-foreground/25", w)}
                delayMs={40 + i * 70}
              />
            </div>
            <Bone className="h-3 w-10" delayMs={60 + i * 70} />
          </div>
        ))}
      </div>
      <WritingFooter label="bars" />
    </>
  );
}

const CHART_BARS = ["h-[38%]", "h-[62%]", "h-[48%]", "h-[78%]", "h-[55%]", "h-[70%]"] as const;

function ChartSkeleton() {
  return (
    <>
      <TitleBone />
      <div className="px-3.5 py-3">
        <div className="relative flex h-36 items-end gap-2 rounded-lg border border-border/40 bg-muted/20 px-3 pb-2 pt-4">
          <div className="pointer-events-none absolute inset-x-3 top-4 bottom-2 flex flex-col justify-between">
            <div className="border-t border-dashed border-border/50" />
            <div className="border-t border-dashed border-border/40" />
            <div className="border-t border-dashed border-border/30" />
          </div>
          {CHART_BARS.map((h, i) => (
            <Bone
              key={i}
              className={cn("relative z-[1] flex-1 rounded-t-sm bg-primary/20", h)}
              delayMs={i * 90}
            />
          ))}
        </div>
      </div>
      <WritingFooter label="chart" />
    </>
  );
}

function CardsSkeleton() {
  return (
    <>
      <TitleBone />
      <div className="grid grid-cols-3 gap-2.5 px-3.5 py-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="space-y-2 rounded-lg border border-border/40 bg-muted/15 px-3 py-3"
          >
            <Bone className="h-2.5 w-12" delayMs={i * 80} />
            <Bone className="h-5 w-16" delayMs={40 + i * 80} />
            <Bone className="h-2 w-10" delayMs={80 + i * 80} />
          </div>
        ))}
      </div>
      <WritingFooter label="cards" />
    </>
  );
}

function UnknownSkeleton() {
  return (
    <>
      <TitleBone />
      <div className="space-y-2.5 px-3.5 py-3">
        <Bone className="h-2.5 w-full" />
        <Bone className="h-2.5 w-[92%]" delayMs={80} />
        <Bone className="h-2.5 w-[78%]" delayMs={160} />
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Bone className="h-16 rounded-lg" delayMs={120} />
          <Bone className="h-16 rounded-lg" delayMs={200} />
          <Bone className="h-16 rounded-lg" delayMs={280} />
        </div>
      </div>
      <WritingFooter label="visual" />
    </>
  );
}

const LABELS: Record<InsightsRenderWidgetKind, string> = {
  bento: "answer",
  table: "table",
  bar: "bars",
  chart: "chart",
  cards: "cards",
  unknown: "visual",
};

function BentoSkeleton() {
  return (
    <>
      <div className="space-y-3 p-2">
        <Bone className="h-14 w-full rounded-xl" />
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Bone key={i} className="h-16 rounded-xl" delayMs={i * 60} />
          ))}
        </div>
        <Bone className="h-36 w-full rounded-xl" delayMs={120} />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Bone className="h-24 rounded-xl" delayMs={160} />
          <Bone className="h-24 rounded-xl" delayMs={200} />
        </div>
      </div>
      <WritingFooter label="answer" />
    </>
  );
}

export function InsightsRenderSkeleton({
  kind = "unknown",
  className,
}: {
  kind?: InsightsRenderWidgetKind;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "my-3 w-full overflow-hidden rounded-xl border border-primary/35 bg-background",
        className,
      )}
      aria-busy="true"
      aria-label={`Loading ${LABELS[kind]}`}
    >
      {kind === "bento" ? (
        <BentoSkeleton />
      ) : kind === "table" ? (
        <TableSkeleton />
      ) : kind === "bar" ? (
        <BarSkeleton />
      ) : kind === "chart" ? (
        <ChartSkeleton />
      ) : kind === "cards" ? (
        <CardsSkeleton />
      ) : (
        <UnknownSkeleton />
      )}
    </div>
  );
}
